import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "@/lib/api";
import { firePurchase, isAdsInstalled } from "@/components/TrackingLoader";
import { useCart } from "@/lib/CartContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";

const CONVERSION_RETRY_INTERVAL_MS = 500;
const CONVERSION_MAX_ATTEMPTS = 10;

export function CheckoutSuccess() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [attempts, setAttempts] = useState(0);
  // Purchase payload captured the moment Stripe confirms `paid`. Passed to
  // the Google Ads retry loop as-is — we never re-poll Stripe during retries.
  const [purchasePayload, setPurchasePayload] = useState(null);
  const cart = useCart();

  // ---- Poll /checkout/status until Stripe confirms `paid` (or 10 tries) ----
  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          console.log("[gads] payment verified", { order_id: data.order_id, session_id: sessionId });
          // Extract enhanced-conversion user data. This is NEVER stored client-side
          // beyond this in-memory payload and NEVER logged. gtag SHA-256 hashes
          // fields client-side before dispatch.
          const ec = data.enhanced_conversion || {};
          setPurchasePayload({
            value: data.amount,
            currency: data.currency,
            orderId: data.order_id || sessionId,
            trackCount: (data.track_ids || []).length || 1,
            productIds: data.track_ids || (data.track_id ? [data.track_id] : []),
            newCustomer: !!data.new_customer,
            paymentIntent: data.payment_intent || "",
            userData: {
              email: ec.email || "",
              phone: ec.phone || "",
              firstName: ec.first_name || "",
              lastName: ec.last_name || "",
              country: ec.country || "",
            },
          });
          // Clear paid items from the cart (safe: only removes matches)
          const paidIds = data.track_ids || (data.track_id ? [data.track_id] : []);
          paidIds.forEach((id) => cart.removeItem(id));
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
        if (count >= 10) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (count >= 3) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ---- Google Ads conversion — retry until gtag is available (max 10) ----
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  useEffect(() => {
    if (!purchasePayload) return;
    let cancelled = false;
    retryAttemptRef.current = 0;

    const tryFire = () => {
      if (cancelled) return;
      retryAttemptRef.current += 1;
      const n = retryAttemptRef.current;
      const gtagAvailable = typeof window !== "undefined" && typeof window.gtag === "function";
      const adsInstalled = isAdsInstalled();
      console.log(`[gads] conversion attempt ${n}/${CONVERSION_MAX_ATTEMPTS}`, { gtagAvailable, adsInstalled });
      const ok = firePurchase(purchasePayload);
      if (ok) {
        console.log(`[gads] conversion fired on attempt ${n}`);
        retryTimerRef.current = null;
        return;
      }
      if (n >= CONVERSION_MAX_ATTEMPTS) {
        console.log(`[gads] gave up after ${n} attempts (Ads destination never registered)`);
        retryTimerRef.current = null;
        return;
      }
      retryTimerRef.current = setTimeout(tryFire, CONVERSION_RETRY_INTERVAL_MS);
    };
    tryFire();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [purchasePayload]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="hairline rounded-2xl p-10 md:p-14 max-w-md w-full text-center bg-white/[0.02]">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-red-brand animate-spin" size={44} />
            <div className="font-anton text-3xl mt-6">{t("checkout.confirming")}</div>
            <p className="text-white/60 mt-2 text-sm">
              {t("checkout.confirmingBody")}
            </p>
            <p className="text-white/40 mt-2 text-[10px] font-mono tracking-widest" dir="ltr">{t("checkout.check", { n: attempts })}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-red-brand" size={56} />
            <div className="font-anton text-3xl mt-6">{t("checkout.paidTitle")}</div>
            <p className="text-white/60 mt-2">{t("checkout.paidBody")}</p>
            <div className="flex gap-3 justify-center mt-8">
              <Link to={localisedTo("/library", lang)} data-testid="success-library" className="btn-primary">{t("checkout.goToLibrary")}</Link>
              <Link to={localisedTo("/", lang)} data-testid="success-home" className="btn-ghost">{t("checkout.keepShopping")}</Link>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="mx-auto text-white/60" size={56} />
            <div className="font-anton text-3xl mt-6">{t("checkout.pendingTitle")}</div>
            <p className="text-white/60 mt-2">
              <Trans
                i18nKey="checkout.pendingBody"
                values={{ email: "bookings@djspyofficial.com" }}
                components={{
                  1: <a href="mailto:bookings@djspyofficial.com" className="text-red-brand underline" />,
                }}
              />
            </p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to={localisedTo("/library", lang)} data-testid="success-library" className="btn-ghost">{t("checkout.checkMyLibrary")}</Link>
              <Link to={localisedTo("/", lang)} data-testid="success-back" className="btn-ghost">{t("checkout.backToStore")}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function CheckoutCancel() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="hairline rounded-2xl p-10 max-w-md w-full text-center bg-white/[0.02]">
        <XCircle className="mx-auto text-white/60" size={56} />
        <div className="font-anton text-3xl mt-6">{t("checkout.cancelledTitle")}</div>
        <p className="text-white/60 mt-2">{t("checkout.cancelledBody")}</p>
        <button onClick={() => navigate(localisedTo("/#music", lang))} data-testid="cancel-back" className="btn-primary mt-6">{t("checkout.backToMusicStore")}</button>
      </div>
    </div>
  );
}
